import { useState, useEffect } from 'react';
import { authService } from '../services/auth';
import { UserProfile, UserTier } from '../types';

/**
 * Custom hook for authentication state management
 */
export function useAuth() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check for existing session and handle OAuth callback
    const initAuth = async () => {
      try {
        console.log('🔐 [Auth] Initializing auth...');
        console.log('🔐 [Auth] Current URL:', window.location.href);
        console.log('🔐 [Auth] Hash:', window.location.hash);
        console.log('🔐 [Auth] Search:', window.location.search);
        console.log('🔐 [Auth] Pathname:', window.location.pathname);
        
        // Check if this is an OAuth/email confirmation callback
        const isAuthCallback = window.location.hash.includes('access_token');
        console.log('🔐 [Auth] Is auth callback:', isAuthCallback);
        
        // Preserve query parameters before cleaning hash
        const queryParams = window.location.search;
        
        if (isAuthCallback) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          console.log('🔐 [Auth] Access token present:', !!hashParams.get('access_token'));
          console.log('🔐 [Auth] Refresh token present:', !!hashParams.get('refresh_token'));
          console.log('🔐 [Auth] Type:', hashParams.get('type'));
        }
        
        // getSession() will handle OAuth callback hash fragments automatically
        console.log('🔐 [Auth] Calling getCurrentUser...');
        const currentUser = await authService.getCurrentUser();
        console.log('🔐 [Auth] Current user:', currentUser ? currentUser.email : 'null');
        
        setUser(currentUser);
        
        if (currentUser) {
          console.log('🔐 [Auth] Fetching user profile...');
          const userProfile = await authService.getUserProfile(currentUser.id);
          console.log('🔐 [Auth] User profile:', userProfile);
          setProfile(userProfile);
          
          // If this was an auth callback, clean up the hash but preserve query params
          if (isAuthCallback) {
            console.log('🔐 [Auth] Auth callback successful, cleaning up hash');
            // Clean up OAuth hash from URL but keep query parameters
            const cleanUrl = window.location.pathname + queryParams;
            window.history.replaceState({}, '', cleanUrl);
            console.log('🔐 [Auth] Cleaned URL:', cleanUrl);
          }
        } else {
          console.log('🔐 [Auth] No user found after auth callback');
        }
      } catch (err) {
        console.error('🔐 [Auth] Error checking user:', err);
      } finally {
        console.log('🔐 [Auth] Auth initialization complete');
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = authService.onAuthStateChange(async (user) => {
      setUser(user);
      
      if (user) {
        const userProfile = await authService.getUserProfile(user.id);
        setProfile(userProfile);
      } else {
        setProfile(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    setError(null);
    const { error, user: newUser } = await authService.signUp(email, password);
    if (error) {
      setError(error.message);
      return false;
    }
    // Update state immediately if user was returned
    if (newUser) {
      setUser(newUser);
      const userProfile = await authService.getUserProfile(newUser.id);
      setProfile(userProfile);
    }
    return true;
  };

  const signIn = async (email: string, password: string) => {
    setError(null);
    const { error, user: newUser } = await authService.signIn(email, password);
    if (error) {
      setError(error.message);
      return false;
    }
    // Update state immediately if user was returned
    if (newUser) {
      setUser(newUser);
      const userProfile = await authService.getUserProfile(newUser.id);
      setProfile(userProfile);
    }
    return true;
  };

  const signInWithGoogle = async () => {
    setError(null);
    const { error } = await authService.signInWithGoogle();
    if (error) {
      setError(error.message);
      return false;
    }
    return true;
  };

  const signOut = async () => {
    setError(null);
    const { error } = await authService.signOut();
    if (error) {
      setError(error.message);
      return false;
    }
    return true;
  };

  const getUserTier = (): UserTier => {
    if (!user) return UserTier.FREE;
    return profile?.tier || UserTier.SIGNED_IN;
  };

  return {
    user,
    profile,
    loading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    getUserTier,
    isAuthenticated: !!user,
  };
}
